import { useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import {
  CHOICE_TYPES,
  MAX_OPTIONS,
  MAX_QUESTIONS,
  NEW_QUESTION_TYPES,
  QUESTION_TYPES,
  WORKSPACE_REQUIREMENTS,
  normalizeRequirementsConfig,
} from "../../utils/workspaceRequirements";
import WorkspaceProfilePreview from "./WorkspaceProfilePreview";
import RequiredDocumentsModal from "./RequiredDocumentsModal";
import { normalizeTenantRequirements } from "../../utils/tenantRequirements";

// { aml: "kyc", document_forensics: "basic_documents" }: an item can only be
// turned on while the item it needs is on
const NEEDS = Object.fromEntries(
  WORKSPACE_REQUIREMENTS.filter((r) => r.needs).map((r) => [r.key, r.needs]),
);

export function Tip({ text }) {
  return (
    <span className="lw-rq-tip" tabIndex={0} data-tip={text} aria-label={text}>
      i
    </span>
  );
}

function Check({ checked, onChange, label, disabled = false }) {
  return (
    <input
      type="checkbox"
      className="lw-rq-check"
      checked={Boolean(checked)}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

// One row: checkbox, name, add-on badge, info tip and optional "Required".
export function Row({
  label,
  tip,
  checked,
  onChange,
  addon,
  disabled,
  required,
  onRequired,
  sub = false,
  extra,
  children,
}) {
  return (
    <div className={`lw-rq-row${sub ? " sub" : ""}${checked ? "" : " off"}`}>
      <div className="lw-rq-head">
        <Check
          checked={checked}
          onChange={onChange}
          label={label}
          disabled={disabled}
        />
        <span className="lw-rq-name">{label}</span>
        {addon && <span className="lw-rq-addon">ADDON</span>}
        {tip && <Tip text={tip} />}
        {extra}
        {onRequired && checked && (
          <label className="lw-rq-required">
            <Check checked={required} onChange={onRequired} label="Required" />
            Required
          </label>
        )}
      </div>
      {children}
    </div>
  );
}

const Group = ({ title, children }) => (
  <>
    <div className="lw-rq-group">{title}</div>
    {children}
  </>
);

export default function WorkspaceRequirementsConfig({ form, onChange }) {
  const [text, setText] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [error, setError] = useState("");
  const [showDocs, setShowDocs] = useState(false);

  const config = form.requirements_config;
  const questions = config.applicant_questions;
  const tenant = form.tenant_requirements;
  const household = tenant.household;

  const setConfig = (patch) =>
    onChange({
      requirements_config: normalizeRequirementsConfig({ ...config, ...patch }),
    });
  const setItem = (key, field, value) =>
    setConfig({ [key]: { ...config[key], [field]: value } });
  const setTenant = (key, field, value) =>
    onChange({
      tenant_requirements: normalizeTenantRequirements({
        ...tenant,
        [key]: { ...tenant[key], [field]: value },
      }),
    });
  // Source of funds: option 1 (Skor template) turns on the client
  // identification form for tenants; option 2 (their own) leaves it off.
  // The backend does not ask the form while KYC is off
  // (`requirements_config.kyc`), so the Skor template needs KYC.
  const clientId = tenant.client_identification;
  const kycOn = config.kyc.active;
  const setSourceOfFunds = ({
    affidavit = form.affidavit_activated,
    skorTemplate,
  }) =>
    onChange({
      affidavit_activated: affidavit,
      tenant_requirements: normalizeTenantRequirements({
        ...tenant,
        client_identification: {
          ...clientId,
          active: affidavit && skorTemplate && kycOn,
        },
      }),
    });
  // Turning KYC off also turns the Skor template (client identification) off
  const setKyc = (value) =>
    onChange({
      requirements_config: normalizeRequirementsConfig({
        ...config,
        kyc: { ...config.kyc, active: value },
      }),
      ...(!value && {
        tenant_requirements: normalizeTenantRequirements({
          ...tenant,
          client_identification: { ...clientId, active: false },
        }),
      }),
    });
  const setQuestions = (patch) =>
    setConfig({ applicant_questions: { ...questions, ...patch } });
  const updateQuestion = (id, patch) =>
    setQuestions({
      custom: questions.custom.map((q) =>
        q.id === id ? { ...q, ...patch } : q,
      ),
    });
  const setInsurance = (key, value) => {
    const next = {
      insurance_service_status: form.insurance_service_status,
      skor_protect_service: form.skor_protect_service,
      [key]: value,
    };
    if (next.skor_protect_service) next.insurance_service_status = true;
    if (!next.insurance_service_status) next.skor_protect_service = false;
    onChange(next);
  };

  // A config item: on/off with an optional "Required" checkbox.
  const item = (key, label, tip, { canRequire = false, addon, extra } = {}) => (
    <Row
      label={label}
      tip={tip}
      addon={addon}
      extra={extra}
      checked={config[key].active}
      onChange={(value) =>
        key === "kyc" ? setKyc(value) : setItem(key, "active", value)
      }
      disabled={Boolean(NEEDS[key]) && !config[NEEDS[key]].active}
      required={config[key].required_before_sharing}
      onRequired={
        canRequire
          ? (value) => setItem(key, "required_before_sharing", value)
          : undefined
      }
    />
  );

  function addQuestion() {
    const list = options
      .split(",")
      .map((option) => option.trim())
      .filter(Boolean);
    if (!text.trim()) return setError("Write the question.");
    if (
      CHOICE_TYPES.includes(type) &&
      (list.length < 2 || list.length > MAX_OPTIONS)
    )
      return setError(`Add 2 to ${MAX_OPTIONS} options, separated by commas.`);
    setQuestions({
      custom: [
        ...questions.custom,
        {
          id: crypto.randomUUID(),
          text: text.trim(),
          type,
          options: CHOICE_TYPES.includes(type) ? list : [],
          active: true,
        },
      ],
    });
    setText("");
    setOptions("");
    setError("");
  }

  return (
    <div className="lw-rq-layout">
      <div className="lw-rq-card">
        <div className="lw-rq-title">
          <h3>Requirements for each tenant</h3>
          <Tip text="Checks Skor runs for every tenant of this workspace. Required items must be completed before a profile can be shared." />
        </div>

        <Group title="Identity">
          {item(
            "kyc",
            "Identity verification (KYC)",
            "Valid ID, liveness, face match and tamper detection.",
          )}
          {item(
            "aml",
            "AML check",
            "Sanctions and politically exposed persons (PEP) screening. Needs Identity verification (KYC).",
            {
              addon: true,
            },
          )}
        </Group>

        <Group title="Documents">
          {item(
            "basic_documents",
            "Basic documents",
            "Skor requests and analyses ID, income and employment documents. Without it, the profile relies on what is declared.",
            {
              extra: (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  style={{ fontSize: 12 }}
                  onClick={() => setShowDocs(true)}
                >
                  See documents
                </button>
              ),
            },
          )}
          {item(
            "document_forensics",
            "Document forensics",
            "Detects tampered or generated documents: metadata, fonts, calculations and cross-document consistency. Needs Basic documents.",
          )}
        </Group>

        <Group title="Solvency">
          {item(
            "defaulter_check",
            "Defaulter check",
            "Checks debtor and rent-default registries.",
            { canRequire: true },
          )}
          {item(
            "financial_assessment",
            "Financial assessment",
            "Income verified with bank statements: rent-to-income and financial Skor.",
            { canRequire: true },
          )}
          {item(
            "open_banking",
            "Open Banking",
            "The applicant connects their bank: full data instantly. Works alongside the financial assessment.",
            {
              canRequire: true,
              addon: true,
            },
          )}
          <Row
            label="Source of funds declaration"
            tip="The applicant signs that their identity is correct and their funds lawful."
            addon
            checked={form.affidavit_activated}
            onChange={(value) =>
              setSourceOfFunds({ affidavit: value, skorTemplate: value })
            }
          >
            {form.affidavit_activated && (
              <div
                className="lw-rq-options"
                role="radiogroup"
                aria-label="Source of funds declaration"
              >
                <div className={`lw-rq-option${clientId.active ? " on" : ""}`}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={clientId.active}
                    disabled={!kycOn}
                    onClick={() => setSourceOfFunds({ skorTemplate: true })}
                  >
                    <FileText size={14} aria-hidden="true" />
                    Skor template
                  </button>
                </div>
                {/* Disabled for now: only the Skor template can be picked */}
                <div className={`lw-rq-option${clientId.active ? "" : " on"}`}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!clientId.active}
                    disabled
                    onClick={() => setSourceOfFunds({ skorTemplate: false })}
                  >
                    Use your own · Contact sales
                  </button>
                </div>
                <small>
                  {!kycOn
                    ? "The Skor template needs Identity verification (KYC). Turn it on to pick it."
                    : clientId.active
                      ? "Tenants fill in and sign the Skor KYC-AML identification form."
                      : "Contact sales to set up your own declaration. Tenants do not get the Skor form."}
                </small>
              </div>
            )}
          </Row>
        </Group>

        <Group title="Demographics">
          <Row
            label="Applicant questions"
            tip="Questions to the applicant about their household. Pick Skor’s or add your own."
            checked={household.active}
            onChange={(value) => setTenant("household", "active", value)}
            required={household.required_before_sharing}
            onRequired={(value) =>
              setTenant("household", "required_before_sharing", value)
            }
          >
            {household.active && (
              <div className="lw-rq-subs">
                <Row
                  sub
                  label="LinkedIn"
                  tip="Link to their professional profile."
                  checked={questions.linkedin}
                  onChange={(value) => setQuestions({ linkedin: value })}
                />
                <Row
                  sub
                  label="Total household members"
                  tip="How many people will live in the home."
                  checked={questions.household_members}
                  onChange={(value) =>
                    setQuestions({ household_members: value })
                  }
                />
                <Row
                  sub
                  label="Number of minors"
                  tip="How many minors will live in the home."
                  checked={questions.minors}
                  onChange={(value) => setQuestions({ minors: value })}
                />
                <Row
                  sub
                  label="Dogs"
                  tip="Whether they have dogs and how many."
                  checked={questions.dogs}
                  onChange={(value) => setQuestions({ dogs: value })}
                />
                {questions.custom.map((question) => (
                  <Row
                    sub
                    key={question.id}
                    label={question.text}
                    tip={`${QUESTION_TYPES.find(([key]) => key === question.type)?.[1]}${
                      question.options.length
                        ? `: ${question.options.join(", ")}`
                        : ""
                    }`}
                    addon
                    checked={question.active}
                    onChange={(value) =>
                      updateQuestion(question.id, { active: value })
                    }
                  >
                    <button
                      type="button"
                      className="lw-rq-delete"
                      aria-label={`Delete question ${question.text}`}
                      onClick={() =>
                        setQuestions({
                          custom: questions.custom.filter(
                            (q) => q.id !== question.id,
                          ),
                        })
                      }
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </Row>
                ))}
                {questions.custom.length < MAX_QUESTIONS && (
                  <div className="lw-rq-add">
                    <input
                      value={text}
                      maxLength={200}
                      placeholder="New question"
                      aria-label="New question"
                      onChange={(event) => setText(event.target.value)}
                    />
                    <select
                      value={type}
                      aria-label="Answer type"
                      onChange={(event) => setType(event.target.value)}
                    >
                      {NEW_QUESTION_TYPES.map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                    {CHOICE_TYPES.includes(type) && (
                      <input
                        value={options}
                        placeholder="Options, separated by commas"
                        aria-label="Options"
                        onChange={(event) => setOptions(event.target.value)}
                      />
                    )}
                    <button type="button" onClick={addQuestion}>
                      + Add question
                    </button>
                  </div>
                )}
                {error && (
                  <p className="lw-error" role="alert">
                    {error}
                  </p>
                )}
              </div>
            )}
          </Row>
        </Group>

        <Group title="Additional guarantees">
          <Row
            label="Insurability check"
            tip="Shows whether the tenant can be insured with Skor Protect and on what terms."
            checked={form.insurance_service_status}
            onChange={(value) =>
              setInsurance("insurance_service_status", value)
            }
          />
          <Row
            label="Rent default insurance"
            tip="Offers the approved tenant Skor Protect rent default insurance. Turns on the insurability check."
            checked={form.skor_protect_service}
            onChange={(value) => setInsurance("skor_protect_service", value)}
          />
          {item(
            "guarantor",
            "Guarantor",
            "Applicants can add guarantors, checked the same way.",
          )}
        </Group>
      </div>
      <WorkspaceProfilePreview form={form} />
      {showDocs && (
        <RequiredDocumentsModal onClose={() => setShowDocs(false)} />
      )}
    </div>
  );
}
